import tw from 'tailwind-styled-components'
import {
  CheckboxColor,
  CheckboxSize
} from '@components/common/base/checkbox-new/types'
import {cn} from '@containers/html-utils'

// NOTE: minmax(0,1fr) is required to prevent grid cell be larger than the parent,
// see https://css-tricks.com/preventing-a-grid-blowout/

export const TwWrapper = tw.div<{
  $disabled?: boolean
  $withLabel?: boolean
  $withFootnote?: boolean
}>`
  group/checkbox
  select-none
  ${p => {
    const cx: string[] = []
    if (!p.$disabled) {
      cx.push('cursor-pointer')
    }
    if (p.$withLabel && p.$withFootnote) {
      cx.push(
        'grid',
        'grid-cols-[16px_minmax(0,1fr)]',
        'grid-rows-[minmax(0,min-content)_minmax(0,min-content)]',
        'gap-x-8',
        'gap-y-4',
        'items-center',
        'justify-items-start'
      )
    } else if (p.$withLabel) {
      cx.push(
        'grid',
        'grid-cols-[16px_minmax(0,1fr)]',
        'gap-x-8',
        'items-center',
        'justify-items-start'
      )
    }
    return cx.join(' ')
  }}
`

export const TwLabel = tw.div<{$disabled?: boolean}>`
  text-14
  leading-4
  text-ellipsis
  whitespace-nowrap
  overflow-hidden
  max-w-full
  ${p => cn(p.$disabled ? 'text-disabled' : 'text-primary')}
`

export const TwFootnote = tw.div<{
  $disabled?: boolean
  $full?: boolean
}>`
  text-12
  leading-4
  col-start-2
  text-ellipsis
  ${p => (p.$full ? 'whitespace-normal' : 'whitespace-nowrap')}
  overflow-hidden
  max-w-full
  ${p => cn(p.$disabled ? 'text-disabled' : 'text-secondary')}
`

const emptyMixin = (
  disabled?: boolean,
  error?: boolean,
  $rounded?: boolean
): string => {
  const cx: string[] = ['shadow-border-1']
  if (disabled) {
    cx.push('shadow-action-disabled', 'bg-background-disabled')
  } else {
    cx.push(
      'bg-background-primary',
      $rounded
        ? 'group-hover/checkbox:shadow-icon-secondary'
        : 'group-hover/checkbox:shadow-line-hover',
      'group-active/checkbox:shadow-normal-gray'
    )
    if (error) {
      cx.push('shadow-status-error')
    } else {
      cx.push($rounded ? 'shadow-icon-secondary' : 'shadow-line-base')
    }
  }
  return cx.join(' ')
}

const afterIconMixin = `
  after:content-[""]
  after:absolute
  after:top-1/2
  after:left-1/2
  after:transform
  after:-translate-x-1/2
  after:-translate-y-1/2
`

const checkIconMixin = `
  ${afterIconMixin}
  after:w-[10px]
  after:h-[8px]
  after:[background-image:url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAxMCA4Ij48cGF0aCBzdHJva2U9IiNmZmYiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgc3Ryb2tlLXdpZHRoPSIxLjMiIGQ9Ik0xLjUgNCA0IDYuNWw1LTUiLz48L3N2Zz4=")]
  after:bg-center
  after:bg-no-repeat
  after:text-inherit
`

const hoveredCheckIconMixin = `
  ${afterIconMixin}
  group-hover/checkbox:after:w-[10px]
  group-hover/checkbox:after:h-[8px]
  group-hover/checkbox:after:[background-image:url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAxMCA4Ij48cGF0aCBzdHJva2U9IiM2NjcwODUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgc3Ryb2tlLXdpZHRoPSIxLjMiIGQ9Ik0xLjUgNCA0IDYuNWw1LTUiLz48L3N2Zz4=")]
  group-hover/checkbox:after:bg-center
  group-hover/checkbox:after:bg-no-repeat
  group-hover/checkbox:after:text-inherit
`

const checkedMixin = (disabled?: boolean, $color?: CheckboxColor): string => {
  const cx: string[] = [checkIconMixin]
  if (disabled) {
    cx.push('bg-action-disabled')
  } else {
    switch ($color) {
      case 'green':
        cx.push(
          'group-hover/checkbox:bg-success',
          'group-active/bg-success',
          'bg-success',
          'group-hover/text-primary'
        )
        break
      case 'gray':
        cx.push(
          'group-hover/checkbox:bg-background-primary',
          'group-active/bg-success',
          'bg-background-primary',
          'group-hover/text-grey'
        )
        break
      default:
        cx.push(
          'group-hover/checkbox:bg-action-hover',
          'group-active/checkbox:bg-action-pressed',
          'bg-action-default'
        )
        break
    }
  }
  return cx.join(' ')
}

// height is the same as check stroke width
const minusIconMixin = `
  ${afterIconMixin}
  after:w-[9px]
  after:h-[1.3px]
  after:rounded-1
  after:bg-icon-invert
`

const indeterminateMixin = (disabled?: boolean): string => {
  const cx: string[] = [minusIconMixin]
  if (disabled) {
    cx.push('bg-action-disabled')
  } else {
    cx.push(
      'group-hover/checkbox:bg-action-hover',
      'group-active/checkbox:bg-action-pressed',
      'bg-action-default'
    )
  }
  return cx.join(' ')
}

const stateMixin = (p: {
  $value: boolean | undefined
  $disabled?: boolean
  $error?: boolean
  $color?: CheckboxColor
  $rounded?: boolean
}): string => {
  switch (p.$value) {
    case false:
      return emptyMixin(p.$disabled, p.$error, p.$rounded)
    case undefined:
      return indeterminateMixin(p.$disabled)
    case true:
      return checkedMixin(p.$disabled, p.$color)
  }
}

const hoverCheckedMixin = (p: {
  $value: boolean | undefined
  $rounded?: boolean
}): string => {
  const cx: string[] = p.$rounded && !p.$value ? [hoveredCheckIconMixin] : []

  return cx.join(' ')
}

export const TwCheckbox = tw.div<{
  $value: boolean | undefined
  $disabled?: boolean
  $error?: boolean
  $color?: CheckboxColor
  $rounded?: boolean
  $size?: CheckboxSize
}>`
  relative
  w-16
  h-16
  ${p => (p.$size === 'big' ? 'w-20 h-20' : '')}
  ${p => (p.$rounded ? 'rounded-full' : 'rounded-4')}
  ${p => stateMixin(p)}
  
  &:hover {
    ${p => hoverCheckedMixin(p)}
  }
`
